import React, { useState, useEffect, useRef } from "react";
import Input from "../../components/ui/Input";
import Button from "../../components/ui/Button";
import ErrorMessage from "../../components/ui/ErrorMessage";
import Badge from "../../components/ui/Badge";
import { TelegramFormData, UserProfile } from "../../types";
import { isValidTelegramUsername } from "../../lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTelegram } from "@fortawesome/free-brands-svg-icons";
import {
  faCheckCircle,
  faTimesCircle,
  faQuestionCircle,
} from "@fortawesome/free-regular-svg-icons";
import { faUnlink } from "@fortawesome/free-solid-svg-icons";

interface TelegramLinkFormProps {
  currentUsername: string | null;
  onSubmit: (data: TelegramFormData) => Promise<void>;
  onUnlink?: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  profile: UserProfile;
}

const TelegramLinkForm: React.FC<TelegramLinkFormProps> = ({
  currentUsername,
  onSubmit,
  onUnlink,
  isLoading,
  error: submissionError,
  profile,
}) => {
  const [username, setUsername] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);

  useEffect(() => {
    setUsername(currentUsername || "");
    setValidationError(null);
    setShowUnlinkConfirm(false);
  }, [currentUsername]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (!username.trim()) {
      setValidationError("Telegram username cannot be empty.");
      return;
    }
    if (!isValidTelegramUsername(username.trim())) {
      setValidationError(
        "Invalid format. Use @username (5-32 letters, numbers)."
      );
      return;
    }
    console.log(username);

    onSubmit({ telegram_username: username.trim() });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    if (validationError) {
      setValidationError(null);
    }
  };

  const handleBlur = () => {
    if (username.trim() && !isValidTelegramUsername(username.trim())) {
      setValidationError(
        "Invalid format. Use @username (5-32 letters, numbers)."
      );
    } else {
      setValidationError(null);
    }
  };

  const handleUnlink = async () => {
    if (!onUnlink) return;
    setShowUnlinkConfirm(false);
    await onUnlink();
  };

  const telegramStatus = profile.telegramIdLinked
    ? "linked"
    : profile.telegram_username
      ? "pending"
      : "not_linked";

  const statusBadge = {
    linked: (
      <Badge status="active" size="sm" className="ml-2">
        Linked
      </Badge>
    ),
    pending: (
      <Badge status="pending" size="sm" className="ml-2">
        Pending
      </Badge>
    ),
    not_linked: (
      <Badge status="inactive" size="sm" className="ml-2">
        Not Linked
      </Badge>
    ),
  }[telegramStatus];

  const statusText = {
    linked: (
      <span className="text-functional-success font-medium ml-1">
        {currentUsername}
      </span>
    ),
    pending: (
      <span className="text-functional-warning font-medium ml-1">
        {currentUsername} (Confirmation Needed)
      </span>
    ),
    not_linked: (
      <span className="text-text-secondary ml-1">
        Your Telegram account is not linked.
      </span>
    ),
  }[telegramStatus];

  return (
    <div className="p-5 sm:p-6 md:p-6 h-full flex flex-col">
      {/* Section Title with Icon */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center mb-5 sm:mb-6 space-y-3 sm:space-y-0">
        <div className="w-10 h-10 sm:w-11 sm:h-11 bg-functional-info/20 rounded-full flex items-center justify-center sm:mr-4 flex-shrink-0">
          <FontAwesomeIcon
            icon={faTelegram}
            className="text-functional-info text-lg sm:text-xl"
          />
        </div>
                 <div className="text-center sm:text-left min-w-0 flex-1">
           <h2 className="text-xl sm:text-2xl font-bold text-text-primary">Telegram Account</h2>
           <p className="text-text-secondary text-xs sm:text-sm leading-relaxed">Connect your Telegram for notifications</p>
         </div>
      </div>

      {/* Enhanced Status Display */}
      <div className="mb-5">
                 <div className="bg-dark-tertiary/50 rounded-xl p-4 border border-dark-border/30">
           <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
             <div className="flex items-center">
               <span className="text-sm font-medium text-golden-accent uppercase tracking-wide mr-3">
                 Status:
               </span>
               {statusBadge}
             </div>
             {telegramStatus === "linked" && (
               <div className="flex items-center text-functional-success">
                 <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                 <span className="text-sm font-medium">Connected</span>
               </div>
             )}
           </div>
                     <div className="mt-3 text-text-primary">
                           {telegramStatus === "linked" ? (
                <div className="flex flex-col space-y-2">
                  <span className="font-medium">Connected as:</span>
                  <span className="text-functional-success font-mono bg-dark-primary/50 px-2 py-1 rounded text-sm break-words w-fit">
                    {currentUsername}
                  </span>
                </div>
              ) : telegramStatus === "pending" ? (
                <div className="space-y-2">
                  <div className="flex items-start">
                    <FontAwesomeIcon icon={faQuestionCircle} className="text-functional-warning mr-2 mt-1 flex-shrink-0" />
                    <span className="font-medium text-functional-warning">Awaiting confirmation for:</span>
                  </div>
                  <div className="ml-6 sm:ml-0">
                    <span className="font-mono bg-dark-primary/50 px-2 py-1 rounded text-sm break-words w-fit">
                      {currentUsername}
                    </span>
                  </div>
                </div>
              ) : (
               <div className="flex items-center">
                 <FontAwesomeIcon icon={faTimesCircle} className="text-text-secondary mr-2 flex-shrink-0" />
                 <span className="text-text-secondary">No Telegram account linked</span>
               </div>
             )}
           </div>
        </div>
      </div>

      {/* Input Section - Only show if not linked */}
      {telegramStatus !== "linked" && (
        <div className="flex-1 flex flex-col">
          <div className="mb-4">
            <Input
              label="Telegram Username"
              id="telegram_username"
              name="telegram_username"
              value={username}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="@your_telegram_username"
              disabled={isLoading}
              required
              error={validationError}
            />
          </div>

          {submissionError && (
            <ErrorMessage message={submissionError} className="mb-4" />
          )}

          {/* Submit Button */}
          <div className="mt-auto">
            <Button
              type="submit"
              isLoading={isLoading}
              onClick={handleSubmit}
              disabled={
                isLoading ||
                !username.trim() ||
                !!validationError ||
                (profile.telegramIdLinked &&
                  username.trim() === currentUsername?.trim())
              }
              variant="primary"
              className="w-full"
              size="md"
            >
              <FontAwesomeIcon icon={faTelegram} className="mr-2" />
              {currentUsername ? "Update Username" : "Link Telegram Account"}
            </Button>
          </div>
        </div>
      )}

      {/* Linked State - Show success message */}
      {telegramStatus === "linked" && (
        <div className="flex-1 flex flex-col">
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-functional-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faCheckCircle} className="text-functional-success text-2xl" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                Successfully Connected!
              </h3>
              <p className="text-text-secondary text-sm">
                Your Telegram account is now linked and ready to receive notifications.
              </p>
            </div>
          </div>

          {onUnlink && (
            <div className="mt-auto">
              {!showUnlinkConfirm ? (
                <Button
                  type="button"
                  onClick={() => setShowUnlinkConfirm(true)}
                  disabled={isLoading}
                  variant="secondary"
                  className="w-full border-functional-danger/50 text-functional-danger hover:bg-functional-danger/10"
                  size="md"
                >
                  <FontAwesomeIcon icon={faUnlink} className="mr-2" />
                  Unlink Telegram Account
                </Button>
              ) : (
                <div className="space-y-3">
                  <p className="text-text-secondary text-sm text-center">
                    Are you sure? You will stop Telegram notifications until you link again.
                  </p>
                  <div className="flex space-x-3">
                    <Button
                      type="button"
                      onClick={() => setShowUnlinkConfirm(false)}
                      disabled={isLoading}
                      variant="secondary"
                      className="flex-1"
                      size="md"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleUnlink}
                      isLoading={isLoading}
                      disabled={isLoading}
                      variant="primary"
                      className="flex-1 bg-functional-danger hover:bg-functional-danger/80"
                      size="md"
                    >
                      Confirm Unlink
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TelegramLinkForm;
